import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FCRAFooterText } from '@/components/layout/FCRADisclaimer';
import {
  Search,
  FileText,
  Zap,
  Shield,
  BarChart3,
  Users,
  ArrowRight,
  CheckCircle,
  Brain,
  Database,
  Globe
} from 'lucide-react';

// Declare type for HubSpot
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hbspt: any;
  }
}

export const MarketingLandingPage: React.FC = () => {
  const formRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Set document title and meta tags for SEO
    document.title = 'CollectABILITY - AI-Powered Debt Collection Intelligence | Smarter Collections, Higher Returns';

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic SEO meta tags
    updateMetaTag('description', 'Transform your debt collection strategy with AI-powered intelligence. Batch processing, web search, and API access for collectability scoring. Coming soon.');
    updateMetaTag('keywords', 'debt collection, AI, collectability scoring, batch processing, debt recovery, collection intelligence, skip tracing');
    updateMetaTag('robots', 'index, follow');

    // Open Graph meta tags
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:title', 'CollectABILITY - AI-Powered Debt Collection Intelligence', true);
    updateMetaTag('og:description', 'Smarter Collections. Higher Returns. Powered by AI. Transform your debt collection strategy with our intelligence platform. Coming soon.', true);
    updateMetaTag('og:image', `${window.location.origin}/stack-logo-1000.png`, true);
    updateMetaTag('og:url', window.location.href, true);
    updateMetaTag('og:site_name', 'CollectABILITY', true);

    // Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', 'CollectABILITY - AI-Powered Debt Collection Intelligence');
    updateMetaTag('twitter:description', 'Smarter Collections. Higher Returns. Powered by AI. Coming soon.');
    updateMetaTag('twitter:image', `${window.location.origin}/stack-logo-1000.png`);

    // Add structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "CollectABILITY",
      "description": "AI-powered debt collection intelligence platform for smarter collections and higher returns. Batch processing, web search, and API access for collectability scoring. Coming soon.",
      "url": window.location.origin,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "name": "CollectABILITY Services",
        "description": "Coming soon - AI-powered debt collection intelligence"
      },
      "creator": {
        "@type": "Organization",
        "name": "CollectABILITY"
      }
    };

    let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(structuredDataScript);
    }
    structuredDataScript.textContent = JSON.stringify(structuredData);

    // Add preload links for performance
    const addPreloadLink = (href: string, as: string) => {
      if (!document.querySelector(`link[rel="preload"][href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = href;
        link.as = as;
        document.head.appendChild(link);
      }
    };

    addPreloadLink('/linear-logo-sm.png', 'image');

    // Initialize HubSpot form
    const initHubSpotForm = () => {
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId: "39894199",
          formId: "5168a2f5-e36d-484e-b13d-bba41c2a35f9",
          region: "na1",
          target: "#hubspot-form-container"
        });
      } else {
        // Retry after a short delay if HubSpot script isn't loaded yet
        setTimeout(initHubSpotForm, 100);
      }
    };

    // Initialize form after a short delay to ensure DOM is ready
    setTimeout(initHubSpotForm, 500);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Marketing Header - No Auth Buttons */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center" aria-label="CollectABILITY Home">
                <img
                  src="/linear-logo-sm.png"
                  alt="CollectABILITY"
                  className="h-10"
                  loading="eager"
                  height="40"
                />
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-4">
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Features</a>
              <a href="#solutions" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Solutions</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 text-sm font-medium">Pricing</a>
              <a href="#about" className="text-slate-600 hover:text-slate-900 text-sm font-medium">About</a>
            </nav>

            {/* Coming Soon Badge */}
            <div className="flex items-center">
              <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium">
                Coming Soon
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-16 pb-20 lg:pt-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
                Smarter Collections.<br />
                Higher Returns.<br />
                <span className="text-blue-600">Powered by AI.</span>
              </h1>

              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Take years of debt collection strategy from our Intelligence data to optimize returns on everything from leads to assets, with a proprietary algorithm that gets you to answers fast.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={scrollToForm}
                  className="bg-blue-600 hover:bg-blue-800 text-white px-8 py-3 rounded-lg font-semibold text-center flex items-center justify-center gap-2 transition-colors"
                >
                  Get Early Access
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={scrollToForm}
                  className="border border-slate-300 hover:border-blue-500 hover:text-blue-600 text-slate-700 px-8 py-3 rounded-lg font-semibold text-center flex items-center justify-center gap-2 transition-colors"
                >
                  Request Demo
                  <Search className="w-4 h-4" />
                </button>
              </div>

              <p className="text-sm text-slate-500 mt-4">
                Revolutionary AI-powered debt collection intelligence • Launching soon
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-8 ">
                <div className="bg-white rounded-lg  p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <div className="flex-1 bg-slate-100 h-6 rounded ml-4"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium">Contact Records</span>
                      </div>
                      <span className="text-sm text-green-600 font-semibold">94.3% Success</span>
                    </div>
                    <div className="bg-slate-50 rounded p-3">
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <div className="font-semibold text-slate-900">15,247</div>
                          <div className="text-slate-500">Total Records</div>
                        </div>
                        <div>
                          <div className="font-semibold text-green-600">14,378</div>
                          <div className="text-slate-500">Processed</div>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-600">$4,313</div>
                          <div className="text-slate-500">Total Cost</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Enhance your current processes
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              You can choose tools in a standalone amount of time with automation steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg p-4  border border-slate-200"
            >
              <div className="w-12 h-10 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Batch Processing</h3>
              <p className="text-slate-600 mb-4">
                Upload CSV files and process thousands of records at once with AI-powered validation, enrichment, and scoring.
              </p>
              <button
                onClick={scrollToForm}
                className="text-blue-600 hover:text-slate-900 font-medium text-sm flex items-center gap-1 transition-colors"
              >
                Get Early Access <ArrowRight className="w-3 h-3" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-lg p-4  border border-slate-200"
            >
              <div className="w-12 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Search className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Web Search Portal</h3>
              <p className="text-slate-600 mb-4">
                Search individual contacts across multiple data sources for instant validation and enrichment results.
              </p>
              <button
                onClick={scrollToForm}
                className="text-blue-600 hover:text-slate-900 font-medium text-sm flex items-center gap-1 transition-colors"
              >
                Get Early Access <ArrowRight className="w-3 h-3" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-lg p-4  border border-slate-200"
            >
              <div className="w-12 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">API Access</h3>
              <p className="text-slate-600 mb-4">
                Integrate our intelligence directly into your applications with RESTful APIs and real-time processing.
              </p>
              <button
                onClick={scrollToForm}
                className="text-blue-600 hover:text-slate-900 font-medium text-sm flex items-center gap-1 transition-colors"
              >
                Get Early Access <ArrowRight className="w-3 h-3" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI-Powered Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              AI-Powered Recovery Scoring for<br />
              Informed Decision Making
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Using our part of the recovery stack CollectABILITY is the go-to solution for smarter debt collections.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Brain className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Advanced Contact Discovery</h3>
                  <p className="text-slate-600 text-sm">
                    Instantly verify 55 data sources and your latest debt collection through AI's proprietary algorithm for smart debt collection.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Database className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Data Enrichment</h3>
                  <p className="text-slate-600 text-sm">
                    Enhanced data via pre-optimized lead opportunities and connect with consumers using complete background intelligence and data mining.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Predictive Intelligence</h3>
                  <p className="text-slate-600 text-sm">
                    Segmented data by intelligence and portfolio/class, asset risk, and cost to provide a complete solution.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div className="w-64 h-64 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                  <div className="w-40 h-40 bg-white rounded-full  flex items-center justify-center">
                    <Brain className="w-16 h-16 text-blue-600" />
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-12 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-12 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <Globe className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Consumer Profile Discovery</h3>
                  <p className="text-slate-600 text-sm">
                    Comprehensive consumer investigations covering relatives, assets, phone numbers, and debt/employer financial information.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Quality Recommendations</h3>
                  <p className="text-slate-600 text-sm">
                    CollectABILITY's Solution recommendations and skip tracing are powered by its proprietary algorithm for maximum effectiveness.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Proprietary Intelligence</h3>
                  <p className="text-slate-600 text-sm">
                    More data from our collection databases, intelligence enrichment, and marketing across multiple industries.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to transform your collections strategy?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Stop chasing dead ends, improve customer experience, and transform your collections strategies with CollectABILITY. Sign up to be notified when we launch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToForm}
              className="bg-blue-600 hover:bg-blue-800 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Get Early Access
            </button>
            <button
              onClick={scrollToForm}
              className="border border-slate-300 hover:border-white text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Request Demo
            </button>
          </div>
        </div>
      </section>

      {/* HubSpot Form Section */}
      <section ref={formRef} className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-lg  p-8 lg:p-12"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Get Early Access to CollectABILITY
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Be among the first to experience the future of AI-powered debt collection intelligence.
                Join our exclusive early access program and get notified when we launch.
              </p>
            </div>

            <div className="max-w-lg mx-auto">
              <div id="hubspot-form-container"></div>
            </div>

            <div className="text-center mt-8">
              <div className="flex items-center justify-center gap-3 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Free early access</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Priority support</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center mb-8">
              <div className="flex items-center" aria-label="CollectABILITY Home">
                <img
                  src="/linear-logo-sm.png"
                  alt="CollectABILITY"
                  className="h-10"
                  loading="lazy"
                  height="48"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="max-w-4xl mx-auto">
              <FCRAFooterText />
            </div>
            <div className="text-center text-slate-600 text-sm">
              <p>&copy; 2024 CollectABILITY. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

